import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Home, ArrowLeft, Search } from "lucide-react";

const PageNotFound = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-slate-50 overflow-hidden relative">
      {/* Background Animated Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div 
          animate={{ rotate: 360, scale: [1, 1.05, 1] }}
          transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
          className="absolute -top-[20%] -left-[10%] w-[70vw] h-[70vw] rounded-full bg-gradient-to-tr from-indigo-100 to-purple-50 opacity-60 blur-3xl"
        />
        <motion.div 
          animate={{ rotate: -360, scale: [1, 1.05, 1] }}
          transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
          className="absolute -bottom-[20%] -right-[10%] w-[60vw] h-[60vw] rounded-full bg-gradient-to-bl from-blue-100 to-cyan-50 opacity-60 blur-3xl"
        />
      </div>

      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, ease: "easeOut" }}
        className="relative z-10 flex flex-col items-center justify-center p-8 text-center max-w-2xl"
      >
        {/* Animated 404 Text */}
        <div className="flex items-center justify-center gap-4 mb-8">
          <motion.div
            initial={{ y: -30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 1, ease: "easeOut" }}
            className="text-8xl md:text-9xl font-black text-transparent bg-clip-text bg-gradient-to-b from-indigo-600 to-purple-600 drop-shadow-sm"
          >
            4
          </motion.div>
          
          {/* Smooth floating Zero */}
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 1, delay: 0.2, ease: "easeOut" }}
            className="relative flex items-center justify-center"
          >
            <motion.div
              animate={{ 
                y: [0, -12, 0],
              }}
              transition={{ 
                duration: 6,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="text-8xl md:text-9xl font-black text-transparent bg-clip-text bg-gradient-to-b from-indigo-600 to-purple-600 drop-shadow-sm"
            >
              0
            </motion.div>
            
            {/* Embedded Search Icon gently moving inside the zero */}
            <motion.div
               animate={{ 
                 rotate: [0, 8, -8, 0],
                 scale: [1, 1.05, 1],
               }}
               transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
               className="absolute inset-0 flex items-center justify-center pointer-events-none"
            >
              <Search className="w-12 h-12 md:w-16 md:h-16 text-indigo-400/20" />
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 1, delay: 0.3, ease: "easeOut" }}
            className="text-8xl md:text-9xl font-black text-transparent bg-clip-text bg-gradient-to-b from-indigo-600 to-purple-600 drop-shadow-sm"
          >
            4
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5, ease: "easeOut" }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-slate-800 mb-4 tracking-tight">
            Oops! Page not found
          </h2>
          <p className="text-slate-500 text-lg mb-10 max-w-md mx-auto leading-relaxed">
            The page you're looking for seems to have wandered off into the digital void.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ duration: 0.2 }}
                className="flex items-center justify-center w-full sm:w-auto gap-2 px-6 py-3 bg-indigo-600 text-white rounded-xl font-semibold shadow-lg shadow-indigo-200 hover:bg-indigo-700 hover:shadow-indigo-300 transition-colors duration-300"
              >
                <Home className="w-5 h-5" />
                Go Home
              </motion.button>
            </Link>
            <motion.button
              onClick={() => navigate(-1)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ duration: 0.2 }}
              className="flex items-center justify-center w-full sm:w-auto gap-2 px-6 py-3 bg-white text-slate-700 rounded-xl font-semibold shadow-sm shadow-slate-200 hover:bg-slate-50 hover:shadow-md border border-slate-200 transition-colors duration-300"
            >
              <ArrowLeft className="w-5 h-5" />
              Go Back
            </motion.button>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default PageNotFound;
